import { DOCUMENT, isPlatformBrowser } from '@angular/common'
import { Directive, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core'
import { GlobalService } from '../global.service'

/**
 * Reports the full content size of its host element (the `.y-page` scroll
 * container) to the embedding host via `window.parent.postMessage`.
 *
 * Payload: `{ site: 'yes', fullscreen: boolean, width: number, height: number }`.
 *
 * Sending is gated by `g.program.options.embeddeb === true` and is checked on
 * every send so a late-loading program still activates reporting.
 *
 * Changes are detected with a MutationObserver (subtree), a ResizeObserver on
 * the host, capture-phase load/error listeners (late image loads), a window
 * resize listener, and a one-shot `document.fonts.ready` re-check. All
 * observation runs outside Angular's zone; measurements are debounced and
 * deduped against the last sent dimensions to avoid feedback loops when the
 * host resizes the iframe.
 */
@Directive({
  selector: '[embedSize]',
  standalone: true
})
export class EmbedSizeDirective implements OnInit, OnDestroy {

  /** Maps to the `fullscreen` flag in the posted payload. */
  @Input() fullscreen = false

  private host: HTMLElement
  private resizeObserver: ResizeObserver | null = null
  private mutationObserver: MutationObserver | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private lastWidth = 0
  private lastHeight = 0
  private captureHandler: ((e: Event) => void) | null = null

  constructor(
    private el: ElementRef<HTMLElement>,
    private g: GlobalService,
    private zone: NgZone,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.host = this.el.nativeElement
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return
    this.zone.runOutsideAngular(() => this.setup())
  }

  private setup(): void {
    const win = this.document.defaultView as Window | null
    if (!win) return

    this.resizeObserver = new ResizeObserver(() => this.schedule())
    this.resizeObserver.observe(this.host)

    this.mutationObserver = new MutationObserver(() => this.schedule())
    this.mutationObserver.observe(this.host, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    })

    const captureLoad = (e: Event) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'IMG' || target.tagName === 'IFRAME')) this.schedule()
    }
    this.captureHandler = captureLoad
    this.host.addEventListener('load', captureLoad, true)
    this.host.addEventListener('error', captureLoad, true)
    win.addEventListener('resize', this.onWindowResize)

    const fonts = (this.document as any).fonts
    if (fonts && typeof fonts.ready === 'object') {
      fonts.ready.then(() => this.schedule())
    }

    this.schedule()
  }

  private onWindowResize = (): void => this.schedule()

  private schedule(): void {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.send(), 150)
  }

  private measure(): { width: number, height: number } {
    const docEl = this.document.documentElement
    const cs = this.document.defaultView ? getComputedStyle(this.host) : null
    const marginBottom = cs ? parseFloat(cs.marginBottom) || 0 : 0

    const width = Math.max(this.host.scrollWidth, this.host.offsetWidth, docEl ? docEl.scrollWidth : 0)
    const contentHeight = Math.max(this.host.scrollHeight, this.host.offsetHeight)
    const height = this.host.offsetTop + contentHeight + marginBottom

    return {
      width: Math.round(width),
      height: Math.round(Math.max(height, docEl ? docEl.scrollHeight : 0))
    }
  }

  private send(): void {
    if (!this.g.program || !this.g.program.options || this.g.program.options.embeddeb !== true) return

    const { width, height } = this.measure()
    if (width === this.lastWidth && height === this.lastHeight) return
    this.lastWidth = width
    this.lastHeight = height

    const win = this.document.defaultView as Window | null
    if (!win || !win.parent) return

    win.parent.postMessage(
      { site: 'yes', fullscreen: this.fullscreen, width, height },
      '*'
    )
  }

  ngOnDestroy(): void {
    if (this.timer) clearTimeout(this.timer)
    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()

    const win = this.document.defaultView as Window | null
    if (win) win.removeEventListener('resize', this.onWindowResize)

    if (this.captureHandler) {
      this.host.removeEventListener('load', this.captureHandler, true)
      this.host.removeEventListener('error', this.captureHandler, true)
      this.captureHandler = null
    }
  }

}
